import React, { FormEventHandler, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import log from 'loglevel'
import { RpcError } from 'grpc-web'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { ListControlsRequest } from '@trustero/trustero-api-web/lib/model/control_pb'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { useControls } from 'src/components/async/model/control/useControls'
import { useFrameworks } from 'src/components/async/Framework/useFrameworks'
import { useFrameworkSections } from 'src/components/async/Framework/useFrameworkSections'
import {
  FrameworkSectionsCSVForm,
  csvRowsToSectionDrafts,
} from 'src/components/ModalForms/Frameworks/FrameworkSectionsCSVForm'
import { ImportFrameworkSectionsCSVRow } from 'src/components/ModalForms/Frameworks/AddFramework/FrameworkSectionsCSV.constants'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import {
  DuplicateSectionNameError,
  useAddFrameworkSections,
} from 'src/pages/Frameworks/frameworks.hooks'
import { ModalForm, ModalFormId } from '../..'

type Props = {
  framework: ComplianceFramework
}

export const AddFrameworkSectionsCSVModal = ({
  framework,
}: Props): JSX.Element => {
  const cfModelId = framework.getModelId()
  const cfRecordId = framework.getId()
  const show = useIsShowModal(ModalFormId.ADD_FRAMEWORK_SECTIONS_CSV)
  const hide = useHideModal({ modalId: ModalFormId.ADD_FRAMEWORK_SECTIONS_CSV })
  const addFrameworkSections = useAddFrameworkSections()

  const { data: frameworksData } = useFrameworks()
  const { data: sectionsData, isLoading: sectionsLoading } =
    useFrameworkSections(cfModelId, show)
  const { data: controlsData, isLoading: controlsLoading } = useControls(
    new ListControlsRequest(),
    show,
  )

  const [csvItems, setCsvItems] = useState<ImportFrameworkSectionsCSVRow[]>([])
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvErrors, setCsvErrors] = useState<string[]>([])
  const [showAttachmentError, setShowAttachmentError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const existingRequirementNames = useMemo(
    () =>
      (frameworksData?.getItemsList() ?? [])
        .filter(
          (f) =>
            f.getComplianceFrameworkId() === cfRecordId ||
            f.getComplianceFrameworkId() === cfModelId,
        )
        .map((f) => f.getName()),
    [frameworksData, cfRecordId, cfModelId],
  )

  const validControlIdSet = useMemo(
    () =>
      new Set((controlsData?.getItemsList() ?? []).map((c) => c.getModelId())),
    [controlsData],
  )

  const closeAndReset = () => {
    setCsvItems([])
    setCsvFile(null)
    setCsvErrors([])
    setShowAttachmentError(false)
    setIsSubmitting(false)
    hide()
  }

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    if (!csvFile) {
      setShowAttachmentError(true)
      return
    }
    if (csvErrors.length > 0) return

    setIsSubmitting(true)
    try {
      await addFrameworkSections({
        complianceFrameworkModelId: cfModelId,
        existingSections: sectionsData?.getSectionsList() ?? [],
        sections: csvRowsToSectionDrafts(csvItems),
      })
      toast.success('Sections added.')
      closeAndReset()
    } catch (err) {
      if (err instanceof DuplicateSectionNameError) {
        setCsvErrors(
          err.duplicateSectionNames.map(
            (name) => `Section "${name}" already exists.`,
          ),
        )
      } else {
        const errorMessage = getErrorMessage(err as RpcError)
        setCsvErrors([errorMessage.message])
        if (errorMessage.shouldLog) {
          log.error('AddFrameworkSectionsCSVModal submit failed', err)
        }
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ModalForm
      show={show}
      hide={closeAndReset}
      formId={ModalFormId.ADD_FRAMEWORK_SECTIONS_CSV}
      size="xl"
      title="Add Sections"
      submitText="Add sections"
      showOverflow
      disableSubmitButton={
        isSubmitting ||
        sectionsLoading ||
        controlsLoading ||
        !csvFile ||
        csvErrors.length > 0
      }
    >
      <form id={ModalFormId.ADD_FRAMEWORK_SECTIONS_CSV} onSubmit={onSubmit}>
        <FrameworkSectionsCSVForm
          existingRequirementNames={existingRequirementNames}
          validControlIdSet={validControlIdSet}
          isSubmitting={isSubmitting}
          isLoadingControls={controlsLoading}
          showAttachmentError={showAttachmentError}
          externalErrors={csvErrors}
          onParseChange={(parsed) => {
            setCsvItems(parsed.items)
            setCsvFile(parsed.file)
            setCsvErrors(parsed.errors)
            if (parsed.file) setShowAttachmentError(false)
          }}
        />
      </form>
    </ModalForm>
  )
}
